import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Spinner, Alert } from "react-bootstrap";
import axios from 'axios';

const RunPipelineTab = ({ apiEndpoint, accessToken }) => {
    const [cutOffDate, setCutOffDate] = useState('');
    const [release, setRelease] = useState('');
    const [confluencePageId, setConfluencePageId] = useState('');
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPipelineRunning, setIsPipelineRunning] = useState(false);
    const [confluenceLink, setConfluenceLink] = useState(null);
    const [pipelineId, setPipelineId] = useState(null);
    const [isDateValid, setIsDateValid] = useState(true);
    const [isReleaseValid, setIsReleaseValid] = useState(true);
    const [isPageIdValid, setIsPageIdValid] = useState(true);

    const confluenceBaseUrl = "https://tui.atlassian.net/wiki/pages/viewpage.action?pageId=";
    const defaultConfluenceLink = "https://tui.atlassian.net/wiki/display/ONEPF/Finance+Release+Deployments";

    useEffect(() => {
        const savedPipelineId = localStorage.getItem("pipelineId");
        const savedConfluenceLink = localStorage.getItem("confluenceLink");
        const savedIsPipelineRunning = JSON.parse(localStorage.getItem("isPipelineRunning") || "false");

        if (savedPipelineId) {
            setPipelineId(savedPipelineId);
            setIsPipelineRunning(savedIsPipelineRunning);
        }

        if (savedConfluenceLink) {
            setConfluenceLink(savedConfluenceLink);
        } else {
            setConfluenceLink(defaultConfluenceLink);
        }
    }, []);

    useEffect(() => {
        if (isPipelineRunning && pipelineId) {
            localStorage.setItem("pipelineId", pipelineId);
            localStorage.setItem("isPipelineRunning", JSON.stringify(isPipelineRunning));
        }
    }, [isPipelineRunning, pipelineId]);

    useEffect(() => {
        if (!isPipelineRunning) {
            localStorage.removeItem("pipelineId");
            localStorage.setItem("isPipelineRunning", "false");
        }
    }, [isPipelineRunning]);

    useEffect(() => {
        if (isPipelineRunning && pipelineId) {
            const interval = setInterval(() => checkPipelineStatus(pipelineId), 15000);
            return () => clearInterval(interval);
        }
    }, [isPipelineRunning, pipelineId]);

    const checkPipelineStatus = async (pipelineId) => {
        try {
            const response = await axios.post(`${apiEndpoint}/pipeline_status`, {
                pipeline_id: pipelineId
            }, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const { status, confluence_link } = response.data;
            console.log(`Pipeline ${pipelineId} status: ${status}`);

            if (status === 'success') {
                setIsPipelineRunning(false);
                setMessage("Confluence page generated!");
                if (confluence_link) {
                    setConfluenceLink(confluence_link);
                    localStorage.setItem("confluenceLink", confluence_link);
                }
            } else if (status === 'failed') {
                setIsPipelineRunning(false);
                setError("Pipeline execution failed.");
                localStorage.removeItem("pipelineId");
                localStorage.setItem("isPipelineRunning", "false");
            }

        } catch (err) {
            console.error("Error checking pipeline status:", err);
            setError("Error checking pipeline status.");
            setIsPipelineRunning(false);
        }
    };

    const validateDate = (date) => {
        const regex = /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
        const [year, month, day] = date.split("-").map(Number);
        const dateObj = new Date(year, month - 1, day);
        return (
            regex.test(date) &&
            dateObj.getFullYear() === year &&
            dateObj.getMonth() + 1 === month &&
            dateObj.getDate() === day
        );
    };

    const handleChangeDate = (e) => {
        const inputValue = e.target.value;
        setCutOffDate(inputValue);
        setIsDateValid(validateDate(inputValue));
    };

    const validateVersion = (version) => {
        const regex = /^\d+_\d+_\d+$/;
        return regex.test(version.trim());
    };

    const handleChangeRelease = (e) => {
        const inputValue = e.target.value.trim();
        setRelease(inputValue);
        setIsReleaseValid(validateVersion(inputValue));
    };

    const validatePageId = (pageId) => {
        if (pageId.trim() === "") return true;
        const regex = /^\d+$/;
        return regex.test(pageId.trim());
    };

    const handleChangePageId = (e) => {
        const inputValue = e.target.value;
        setConfluencePageId(inputValue);
        setIsPageIdValid(validatePageId(inputValue));
    };

    const runPipeline = async () => {
        setError(null);

        const trimmedPageId = confluencePageId.trim();
        const isDateOK = validateDate(cutOffDate.trim());
        const isVersionOK = validateVersion(release.trim());
        const isPageIdOK = validatePageId(trimmedPageId);

        setIsDateValid(isDateOK);
        setIsReleaseValid(isVersionOK);
        setIsPageIdValid(isPageIdOK);

        if (!cutOffDate.trim() || !release.trim() || !isDateOK || !isVersionOK || !isPageIdOK) {
            alert("Please enter valid Cut Off Date, Release version, and Page ID.");
            return;
        }

        setIsLoading(true);
        setMessage("Started generating Confluence page");
        setPipelineId(null);
        setIsPipelineRunning(true);

        try {
            const response = await axios.post(
                `${apiEndpoint}/run_pipeline`,
                {
                    variables: {
                        CUT_OFF_DATE: cutOffDate,
                        RELEASE: release,
                        ...(confluencePageId && { PAGE: confluencePageId })
                    }
                },
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const { pipeline_id, confluence_link } = response.data;
            setPipelineId(pipeline_id);

            if (confluencePageId) {
                const newLink = `${confluenceBaseUrl}${confluencePageId}`;
                setConfluenceLink(newLink);
                localStorage.setItem("confluenceLink", newLink);
            } else if (confluence_link) {
                setConfluenceLink(confluence_link);
                localStorage.setItem("confluenceLink", confluence_link);
            }

            localStorage.setItem("pipelineId", pipeline_id);
            localStorage.setItem("isPipelineRunning", "true");

            setCutOffDate('');
            setRelease('');
            setConfluencePageId('');

        } catch (err) {
            console.error("Error response:", err.response);
            const errorMsg = err.response?.data?.error || "Unexpected error occurred.";
            setError(`Failed to generate Confluence page: ${errorMsg}`);
            setIsPipelineRunning(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container className="text-center">
            <h2>Generate Release page</h2>
            {message && <Alert variant="success">{message}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}

            <Alert variant="info">
                <a href={confluenceLink} target="_blank" rel="noopener noreferrer">
                    View Confluence Page
                </a>
            </Alert>

            <Form>
                <Form.Group className="mb-3">
                    <Form.Label>Cut Off Date</Form.Label>
                    <Form.Control
                        type="text"
                        value={cutOffDate}
                        onChange={handleChangeDate}
                        placeholder="format YYYY-MM-DD"
                        style={{ border: isDateValid ? "1px solid #ced4da" : "1px solid red" }}
                    />
                    {!isDateValid && <small style={{ color: "red" }}>Invalid date format</small>}
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Release version</Form.Label>
                    <Form.Control
                        type="text"
                        value={release}
                        onChange={handleChangeRelease}
                        placeholder="e.g. 43_3_0"
                        style={{ border: isReleaseValid ? "1px solid #ced4da" : "1px solid red" }}
                    />
                    {!isReleaseValid && <small style={{ color: "red" }}>Invalid release version format</small>}
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Confluence Page ID to post</Form.Label>
                    <Form.Control
                        type="text"
                        value={confluencePageId}
                        onChange={handleChangePageId}
                        placeholder="e.g. 12345678"
                        style={{ border: isPageIdValid ? "1px solid #ced4da" : "1px solid red" }}
                    />
                    {!isPageIdValid && <small style={{ color: "red" }}>Confluence Page ID must contain only digits</small>}
                </Form.Group>

                <Button
                    variant={isPipelineRunning ? "warning" : "primary"}
                    onClick={runPipeline}
                    disabled={isLoading ||
                        isPipelineRunning ||
                        !isDateValid ||
                        !isReleaseValid ||
                        !isPageIdValid ||
                        !cutOffDate.trim() ||
                        !release.trim() ||
                        !confluencePageId.trim()
                    }
                >
                    {isLoading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            &nbsp; In progress...
                        </>
                    ) : isPipelineRunning ? "In progress..." : "Generate Confluence page"}
                </Button>
            </Form>
        </Container>
    );
};

export default RunPipelineTab;
